import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, LogOut, ChevronDown, Settings } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { t } from '@/i18n/ro';
import { Avatar } from '@/components/common';
import { NotificationBell } from '@/components/notifications/NotificationBell';
import { SearchModal } from '@/components/search';

export function Header() {
  const { user, logout, instanceUrl } = useAuth();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);

  // ==========================================================================
  //  Căutarea din antet.
  //
  //  Ce era înainte aici: un câmp cu dropdown propriu, care interoga
  //  `globalSearch` -> /work_packages (OpenProject). Tabela `work_packages` are
  //  0 rânduri în producție, deci câmpul nu găsea NIMIC — și nici nu putea, iar
  //  drepturile veneau de la API-ul OpenProject, nu de la regula casei.
  //  Acum antetul nu mai caută el: deschide overlay-ul unic (SearchModal), care
  //  merge pe /auth/data/search, unde accesul se filtrează pe proiect.
  //
  //  De ce BUTON și nu câmp de text: overlay-ul își golește interogarea la
  //  fiecare deschidere (SearchModal, `useLayoutEffect`), deci literele tastate
  //  într-un câmp din antet s-ar pierde la deschidere. Butonul e și ținta
  //  corectă pentru restaurarea focusului la închiderea dialogului.
  //
  //  Starea și scurtătura stau AICI, într-un singur loc: `SearchModal` își
  //  înregistrează Ctrl+K global doar în varianta necontrolată, iar noi îl
  //  montăm controlat. O a doua montare (în Layout) ar da două overlay-uri și o
  //  scurtătură care comută de două ori.
  // ==========================================================================
  const [cautareDeschisa, setCautareDeschisa] = useState(false);

  useEffect(() => {
    const laTasta = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setCautareDeschisa((deschis) => !deschis);
      }
    };
    document.addEventListener('keydown', laTasta);
    return () => document.removeEventListener('keydown', laTasta);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userName = user
    ? `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.login
    : '';

  return (
    <>
    <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6">
      {/* Căutare — deschide overlay-ul */}
      <div className="flex-1 max-w-xl">
        <button
          type="button"
          onClick={() => setCautareDeschisa(true)}
          aria-haspopup="dialog"
          aria-expanded={cautareDeschisa}
          className="w-full flex items-center gap-3 px-4 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors text-left"
        >
          <Search className="w-5 h-5 text-gray-400 flex-shrink-0" aria-hidden="true" />
          <span className="flex-1 text-sm text-gray-500 truncate">
            {t('search.placeholder')}
          </span>
          <kbd className="hidden sm:block text-xs bg-white border border-gray-200 px-1.5 py-0.5 rounded text-gray-500">
            Ctrl K
          </kbd>
        </button>
      </div>

      {/* Right section */}
      <div className="flex items-center gap-4 ml-4">
        {/* Instance URL indicator */}
        {instanceUrl && (
          <span className="text-xs text-gray-400 hidden lg:block">
            {new URL(instanceUrl).hostname}
          </span>
        )}

        {/* Clopoțel notificări */}
        <NotificationBell />

        {/* User menu */}
        <div className="relative">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <Avatar name={userName} size="sm" />
            <span className="text-sm font-medium text-gray-700 hidden sm:block">
              {userName}
            </span>
            <ChevronDown className="w-4 h-4 text-gray-400" />
          </button>

          {/* Dropdown menu */}
          {showUserMenu && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowUserMenu(false)}
              />
              <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-20">
                <div className="px-4 py-2 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-900">
                    {userName}
                  </p>
                  <p className="text-xs text-gray-500">{user?.email}</p>
                </div>

                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    navigate('/settings');
                  }}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Settings className="w-4 h-4" />
                  Setări
                </button>

                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  {t('nav.logout')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>

    {/* Overlay-ul stă în AFARA lui <header>: e `fixed inset-0`, deci nu are ce
        căuta între cele două coloane ale antetului, indiferent că poziționarea
        fixă îl scoate oricum din flux. */}
    <SearchModal isOpen={cautareDeschisa} onClose={() => setCautareDeschisa(false)} />
    </>
  );
}
